"use client";
import { useState, useEffect, useRef } from 'react';
import { useTransition, animated, useSpring } from '@react-spring/web';
import { X, Send, Loader2, UserCheck } from 'lucide-react';
import { GOOGLE_SCRIPT_URL } from '@/lib/config';
import { trackRegistrationStarted, trackRegistrationCompleted, trackFormAbandon } from '@/components/PixelTracker';

interface RegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function RegistrationModal({ isOpen, onClose }: RegistrationModalProps) {
  const [formData, setFormData] = useState({
    nombreApellidos: '',
    correo: '',
    telefono: '',
    ciudadPais: '',
    momentoActual: '',
    motivacion: '',
    relacionValores: '',
    principalCambio: '',
    experienciaPrevia: '',
    comoConociste: '',
    porQueBuenCandidato: '',
    aporteGrupo: '',
    compromisoPersonal: '',
    prioridadContacto: '',
    comentariosAdicionales: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const formStarted = useRef(false);
  const fieldsModified = useRef(new Set<string>());

  useEffect(() => {
    if (isOpen) {
      trackRegistrationStarted();
      formStarted.current = false;
      fieldsModified.current.clear();
    }
  }, [isOpen]);

  // Track form abandonment when modal closes
  useEffect(() => {
    return () => {
      if (!isOpen && formStarted.current && !showSuccess) {
        // User started filling the form but didn't complete it
        trackFormAbandon('Formulario de Registro', fieldsModified.current.size);
      }
    };
  }, [isOpen, showSuccess]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    // Mark that form has been started
    if (!formStarted.current && value.trim()) {
      formStarted.current = true;
    }

    // Track which fields have been modified
    if (value.trim()) {
      fieldsModified.current.add(name);
    }

    if (name === 'telefono') {
      const cleanedValue = value.replace(/[^+\d]/g, '');
      setFormData(prev => ({ ...prev, telefono: cleanedValue }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Required fields validation
    const requiredFields = [
      { field: 'nombreApellidos', message: 'El nombre y apellidos son obligatorios' },
      { field: 'correo', message: 'El correo electrónico es obligatorio' },
      { field: 'telefono', message: 'El teléfono es obligatorio' },
      { field: 'ciudadPais', message: 'La ciudad y país son obligatorios' },
      { field: 'momentoActual', message: 'Este campo es obligatorio' },
      { field: 'motivacion', message: 'Este campo es obligatorio' },
      { field: 'relacionValores', message: 'Este campo es obligatorio' },
      { field: 'principalCambio', message: 'Este campo es obligatorio' },
      { field: 'experienciaPrevia', message: 'Este campo es obligatorio' },
      { field: 'comoConociste', message: 'Este campo es obligatorio' },
      { field: 'porQueBuenCandidato', message: 'Este campo es obligatorio' },
      { field: 'aporteGrupo', message: 'Este campo es obligatorio' },
      { field: 'compromisoPersonal', message: 'Este campo es obligatorio' },
      { field: 'prioridadContacto', message: 'Debes seleccionar una prioridad' }
    ];

    requiredFields.forEach(({ field, message }) => {
      if (!formData[field as keyof typeof formData].trim()) {
        newErrors[field] = message;
      }
    });

    // Email validation
    if (formData.correo && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.correo)) {
      newErrors.correo = 'El correo electrónico no es válido';
    }

    // Phone validation
    if (formData.telefono && !/^\+?\d+$/.test(formData.telefono)) {
      newErrors.telefono = 'El teléfono debe contener solo números y puede empezar con +';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const url = GOOGLE_SCRIPT_URL;

      const params = new URLSearchParams();
      params.append('formType', 'registration');
      Object.entries(formData).forEach(([key, value]) => {
        params.append(key, value);
      });

      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString()
      });

      const data = await response.text();
      console.log('Google Sheets response:', data);

      // Track successful registration
      trackRegistrationCompleted();

      setShowSuccess(true);
      setTimeout(() => {
        setFormData({
          nombreApellidos: '',
          correo: '',
          telefono: '',
          ciudadPais: '',
          momentoActual: '',
          motivacion: '',
          relacionValores: '',
          principalCambio: '',
          experienciaPrevia: '',
          comoConociste: '',
          porQueBuenCandidato: '',
          aporteGrupo: '',
          compromisoPersonal: '',
          prioridadContacto: '',
          comentariosAdicionales: ''
        });
        setShowSuccess(false);
        onClose();
      }, 3000);

    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Hubo un error al enviar tu solicitud. Por favor, inténtalo de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const transitions = useTransition(isOpen, {
    from: { opacity: 0, transform: 'scale(0.95)' },
    enter: { opacity: 1, transform: 'scale(1)' },
    leave: { opacity: 0, transform: 'scale(0.95)' },
    config: { duration: 300 }
  });

  const successSpring = useSpring({
    opacity: showSuccess ? 1 : 0,
    transform: showSuccess ? 'translateY(0px)' : 'translateY(20px)'
  });

  return (
    <>
      {transitions((style, item) =>
        item ? (
          <>
            {/* Backdrop */}
            <animated.div
              style={{ opacity: style.opacity }}
              onClick={onClose}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            />

            {/* Modal */}
            <animated.div
              style={style}
              className="fixed inset-0 bg-transparent z-50 overflow-hidden overflow-y-auto"
              role="dialog"
              aria-modal="true"
              aria-labelledby="registration-modal-title"
            >
              <div className="min-h-screen flex flex-col justify-center py-4 px-4 sm:px-6 lg:px-8">
                <div className="w-full max-w-2xl mx-auto">
                  {/* Header */}
                  <div className="relative bg-gradient-to-r from-[#97d700] to-[#7fb500] p-4 sm:p-6 text-center rounded-t-3xl">
                    <button
                      onClick={onClose}
                      className="absolute right-4 top-4 p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
                      aria-label="Cerrar formulario de registro"
                    >
                      <X className="w-5 h-5 text-white" />
                    </button>

                    <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <UserCheck className="w-8 h-8 text-[#97d700]" />
                    </div>

                    <h2 id="registration-modal-title" className="text-xl sm:text-2xl font-heading uppercase tracking-wider text-white mb-2">
                      Entrevista previa admisión
                    </h2>
                    <p className="text-sm sm:text-base text-white/90">
                      Certificación Internacional Coaching x Valores
                    </p>
                  </div>

                  {/* Form */}
                  <form onSubmit={handleSubmit} className="bg-white p-4 sm:p-6 space-y-4 rounded-b-3xl shadow-xl max-h-[70vh] overflow-y-auto">
                    {/* Introduction */}
                    <div className="bg-gray-50 p-4 rounded-xl text-sm text-gray-700 mb-4">
                      <p className="mb-2">
                        Este formulario es para optar a la Certificación Internacional de Coaching x Valores que se celebrará en Alicante (España) los días 18, 19 y 20 de Julio.
                      </p>
                      <p className="mb-2">
                        Una oportunidad única para vivir esta experiencia en un formato retiro exclusivo y limitado.
                      </p>
                      <p className="mb-2">
                        Las plazas están limitadas a solamente 45 pax que se atenderán por orden de reserva/pago.
                      </p>
                      <p className="font-bold mb-2">
                        SOLO LAS 30 PRIMERAS TIENEN INCLUIDO EL BONUS DE AMAROO (Alojamiento y pensión completa viernes, sábado y domingo). valorado en 525€ +IVA por persona.
                      </p>
                      <p className="mb-2">
                        El resto de plazas debe gestionar su propio alojamiento.
                      </p>
                      <p>
                        En caso de necesitar algo en concreto o querer aportar más información escríbenos a contacto@tonogalan.com
                      </p>
                    </div>

                    {/* Fields */}
                    {/* Nombre y Apellidos */}
                    <div>
                      <label htmlFor="nombreApellidos" className="block text-sm font-medium text-gray-700 mb-1">
                        Nombre y Apellidos <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="nombreApellidos"
                        name="nombreApellidos"
                        value={formData.nombreApellidos}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 rounded-xl border ${
                          errors.nombreApellidos ? 'border-red-500' : 'border-gray-300'
                        } focus:outline-none focus:ring-2 focus:ring-[#97d700] focus:border-transparent transition-all`}
                      />
                      {errors.nombreApellidos && (
                        <p className="text-red-500 text-sm mt-1">{errors.nombreApellidos}</p>
                      )}
                    </div>

                    {/* Correo */}
                    <div>
                      <label htmlFor="correo" className="block text-sm font-medium text-gray-700 mb-1">
                        Dirección de correo electrónico <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="correo"
                        name="correo"
                        value={formData.correo}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 rounded-xl border ${
                          errors.correo ? 'border-red-500' : 'border-gray-300'
                        } focus:outline-none focus:ring-2 focus:ring-[#97d700] focus:border-transparent transition-all`}
                      />
                      {errors.correo && (
                        <p className="text-red-500 text-sm mt-1">{errors.correo}</p>
                      )}
                    </div>

                    {/* Teléfono */}
                    <div>
                      <label htmlFor="telefono" className="block text-sm font-medium text-gray-700 mb-1">
                        Nº de Teléfono <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        id="telefono"
                        name="telefono"
                        value={formData.telefono}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 rounded-xl border ${
                          errors.telefono ? 'border-red-500' : 'border-gray-300'
                        } focus:outline-none focus:ring-2 focus:ring-[#97d700] focus:border-transparent transition-all`}
                        placeholder="+34600123456"
                      />
                      {errors.telefono && (
                        <p className="text-red-500 text-sm mt-1">{errors.telefono}</p>
                      )}
                    </div>

                    {/* Ciudad y País */}
                    <div>
                      <label htmlFor="ciudadPais" className="block text-sm font-medium text-gray-700 mb-1">
                        Ciudad y país de residencia <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="ciudadPais"
                        name="ciudadPais"
                        value={formData.ciudadPais}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 rounded-xl border ${
                          errors.ciudadPais ? 'border-red-500' : 'border-gray-300'
                        } focus:outline-none focus:ring-2 focus:ring-[#97d700] focus:border-transparent transition-all`}
                      />
                      {errors.ciudadPais && (
                        <p className="text-red-500 text-sm mt-1">{errors.ciudadPais}</p>
                      )}
                    </div>

                    {/* Momento Actual */}
                    <div>
                      <label htmlFor="momentoActual" className="block text-sm font-medium text-gray-700 mb-1">
                        ¿Cómo describirías tu momento actual personal y profesional? <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        id="momentoActual"
                        name="momentoActual"
                        value={formData.momentoActual}
                        onChange={handleInputChange}
                        rows={3}
                        className={`w-full px-4 py-3 rounded-xl border ${
                          errors.momentoActual ? 'border-red-500' : 'border-gray-300'
                        } focus:outline-none focus:ring-2 focus:ring-[#97d700] focus:border-transparent transition-all`}
                      />
                      {errors.momentoActual && (
                        <p className="text-red-500 text-sm mt-1">{errors.momentoActual}</p>
                      )}
                    </div>

                    {/* Motivación */}
                    <div>
                      <label htmlFor="motivacion" className="block text-sm font-medium text-gray-700 mb-1">
                        <strong>¿Qué te ha motivado a querer certificarte en Coaching x Valores?</strong> <em>(Queremos conocer tu porqué profundo)</em> <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        id="motivacion"
                        name="motivacion"
                        value={formData.motivacion}
                        onChange={handleInputChange}
                        rows={3}
                        className={`w-full px-4 py-3 rounded-xl border ${
                          errors.motivacion ? 'border-red-500' : 'border-gray-300'
                        } focus:outline-none focus:ring-2 focus:ring-[#97d700] focus:border-transparent transition-all`}
                      />
                      {errors.motivacion && (
                        <p className="text-red-500 text-sm mt-1">{errors.motivacion}</p>
                      )}
                    </div>

                    {/* Relación con Valores */}
                    <div>
                      <label htmlFor="relacionValores" className="block text-sm font-medium text-gray-700 mb-1">
                        <strong>¿Cuál crees que es tu relación actual con tus valores?</strong> <em>(Ej. los tienes claros, los intuyes pero no los nombras, sientes que necesitas reconectarte con ellos, etc.)</em> <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        id="relacionValores"
                        name="relacionValores"
                        value={formData.relacionValores}
                        onChange={handleInputChange}
                        rows={3}
                        className={`w-full px-4 py-3 rounded-xl border ${
                          errors.relacionValores ? 'border-red-500' : 'border-gray-300'
                        } focus:outline-none focus:ring-2 focus:ring-[#97d700] focus:border-transparent transition-all`}
                      />
                      {errors.relacionValores && (
                        <p className="text-red-500 text-sm mt-1">{errors.relacionValores}</p>
                      )}
                    </div>

                    {/* Principal Cambio */}
                    <div>
                      <label htmlFor="principalCambio" className="block text-sm font-medium text-gray-700 mb-1">
                        <strong>¿Cuál es el principal cambio que esperas integrar en tu vida personal o profesional gracias a esta certificación?</strong> <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        id="principalCambio"
                        name="principalCambio"
                        value={formData.principalCambio}
                        onChange={handleInputChange}
                        rows={3}
                        className={`w-full px-4 py-3 rounded-xl border ${
                          errors.principalCambio ? 'border-red-500' : 'border-gray-300'
                        } focus:outline-none focus:ring-2 focus:ring-[#97d700] focus:border-transparent transition-all`}
                      />
                      {errors.principalCambio && (
                        <p className="text-red-500 text-sm mt-1">{errors.principalCambio}</p>
                      )}
                    </div>

                    {/* Experiencia Previa */}
                    <div>
                      <label htmlFor="experienciaPrevia" className="block text-sm font-medium text-gray-700 mb-1">
                        <strong>¿Tienes experiencia previa en procesos de acompañamiento, coaching, desarrollo personal o acompañamiento a otros?</strong> <em>(No es obligatorio tenerla, pero nos ayudará a conocerte mejor)</em> <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        id="experienciaPrevia"
                        name="experienciaPrevia"
                        value={formData.experienciaPrevia}
                        onChange={handleInputChange}
                        rows={3}
                        className={`w-full px-4 py-3 rounded-xl border ${
                          errors.experienciaPrevia ? 'border-red-500' : 'border-gray-300'
                        } focus:outline-none focus:ring-2 focus:ring-[#97d700] focus:border-transparent transition-all`}
                      />
                      {errors.experienciaPrevia && (
                        <p className="text-red-500 text-sm mt-1">{errors.experienciaPrevia}</p>
                      )}
                    </div>

                    {/* Cómo Conociste */}
                    <div>
                      <label htmlFor="comoConociste" className="block text-sm font-medium text-gray-700 mb-1">
                        <strong>¿Cómo supiste de esta certificación?</strong> <em>(Persona de referencia, redes sociales, web, evento, etc.)</em> <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="comoConociste"
                        name="comoConociste"
                        value={formData.comoConociste}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 rounded-xl border ${
                          errors.comoConociste ? 'border-red-500' : 'border-gray-300'
                        } focus:outline-none focus:ring-2 focus:ring-[#97d700] focus:border-transparent transition-all`}
                      />
                      {errors.comoConociste && (
                        <p className="text-red-500 text-sm mt-1">{errors.comoConociste}</p>
                      )}
                    </div>

                    {/* Por Qué Buen Candidato */}
                    <div>
                      <label htmlFor="porQueBuenCandidato" className="block text-sm font-medium text-gray-700 mb-1">
                        <strong>¿Por qué crees que eres un/a buen/a candidato/a para esta certificación?</strong> <em>(Cuéntanos desde tu experiencia, tus inquietudes y tu mirada única)</em> <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        id="porQueBuenCandidato"
                        name="porQueBuenCandidato"
                        value={formData.porQueBuenCandidato}
                        onChange={handleInputChange}
                        rows={3}
                        className={`w-full px-4 py-3 rounded-xl border ${
                          errors.porQueBuenCandidato ? 'border-red-500' : 'border-gray-300'
                        } focus:outline-none focus:ring-2 focus:ring-[#97d700] focus:border-transparent transition-all`}
                      />
                      {errors.porQueBuenCandidato && (
                        <p className="text-red-500 text-sm mt-1">{errors.porQueBuenCandidato}</p>
                      )}
                    </div>

                    {/* Aporte al Grupo */}
                    <div>
                      <label htmlFor="aporteGrupo" className="block text-sm font-medium text-gray-700 mb-1">
                        <strong>¿Cómo imaginas que vas a aportar valor al grupo y a la comunidad durante la certificación?</strong> <em>(Piensa en lo que puedes ofrecer desde tu historia, tu energía, tu talento)</em> <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        id="aporteGrupo"
                        name="aporteGrupo"
                        value={formData.aporteGrupo}
                        onChange={handleInputChange}
                        rows={3}
                        className={`w-full px-4 py-3 rounded-xl border ${
                          errors.aporteGrupo ? 'border-red-500' : 'border-gray-300'
                        } focus:outline-none focus:ring-2 focus:ring-[#97d700] focus:border-transparent transition-all`}
                      />
                      {errors.aporteGrupo && (
                        <p className="text-red-500 text-sm mt-1">{errors.aporteGrupo}</p>
                      )}
                    </div>

                    {/* Compromiso Personal */}
                    <div>
                      <label htmlFor="compromisoPersonal" className="block text-sm font-medium text-gray-700 mb-1">
                        <strong>¿Qué compromiso personal estás dispuesto/a a asumir para aprovechar esta experiencia al máximo?</strong> <em>(Aquí queremos sentir tu determinación y entrega)</em> <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        id="compromisoPersonal"
                        name="compromisoPersonal"
                        value={formData.compromisoPersonal}
                        onChange={handleInputChange}
                        rows={3}
                        className={`w-full px-4 py-3 rounded-xl border ${
                          errors.compromisoPersonal ? 'border-red-500' : 'border-gray-300'
                        } focus:outline-none focus:ring-2 focus:ring-[#97d700] focus:border-transparent transition-all`}
                      />
                      {errors.compromisoPersonal && (
                        <p className="text-red-500 text-sm mt-1">{errors.compromisoPersonal}</p>
                      )}
                    </div>

                    {/* Prioridad Contacto */}
                    <div>
                      <label htmlFor="prioridadContacto" className="block text-sm font-medium text-gray-700 mb-1">
                        ¿Qué Prioridad tienes para que te contactemos? (Donde 5 es la más alta) <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="prioridadContacto"
                        name="prioridadContacto"
                        value={formData.prioridadContacto}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 rounded-xl border ${
                          errors.prioridadContacto ? 'border-red-500' : 'border-gray-300'
                        } focus:outline-none focus:ring-2 focus:ring-[#97d700] focus:border-transparent transition-all`}
                      >
                        <option value="">Selecciona una opción</option>
                        <option value="1">1</option>
                        <option value="2">2</option>
                        <option value="3">3</option>
                        <option value="4">4</option>
                        <option value="5">5</option>
                      </select>
                      {errors.prioridadContacto && (
                        <p className="text-red-500 text-sm mt-1">{errors.prioridadContacto}</p>
                      )}
                    </div>

                    {/* Comentarios Adicionales */}
                    <div>
                      <label htmlFor="comentariosAdicionales" className="block text-sm font-medium text-gray-700 mb-1">
                        Si hay algo más que quieras decirnos, este es tu espacio. Estamos para escucharte.
                      </label>
                      <textarea
                        id="comentariosAdicionales"
                        name="comentariosAdicionales"
                        value={formData.comentariosAdicionales}
                        onChange={handleInputChange}
                        rows={3}
                        className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#97d700] focus:border-transparent transition-all"
                      />
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={`w-full py-4 bg-[#97d700] text-black font-heading uppercase tracking-wider font-bold rounded-full shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300 flex items-center justify-center gap-2 ${
                        isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
                      }`}
                      aria-label={isSubmitting ? "Enviando solicitud..." : "Enviar mi solicitud"}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          <span>Enviando solicitud...</span>
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          <span>Enviar mi solicitud</span>
                        </>
                      )}
                    </button>

                    {/* Success Message */}
                    {showSuccess && (
                      <animated.div
                        style={successSpring}
                        className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-xl text-center"
                      >
                        <p className="font-bold">¡Solicitud enviada con éxito!</p>
                        <p className="text-sm">Te contactaremos en menos de 48 horas.</p>
                      </animated.div>
                    )}
                  </form>
                </div>
              </div>
            </animated.div>
          </>
        ) : null
      )}
    </>
  );
}